'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { setCookie } from 'cookies-next';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { acceptedCookiePolicyAtom, cookieModalAtom } from '~/atoms';
import {
  AcceptedCookiePolicyEnum,
  CookieSettingType,
  CookieTypeEnum,
} from '~/types-and-enums/cookieTypes';
import { cookieSettings, getInitiallySelectedCookies } from '~/utils/cookieUtils';
import CookieCard from './CookieCard';
import CookiePreferences from './CookiePreferences';
import { CookieStorageKey } from './constants';

/**
 * Renders a cookie banner with Accept All or Customise user cookie preferences.
 */
const CookieBanner = () => {
  const [acceptedCookiePolicy, setAcceptedCookiePolicy] = useAtom(acceptedCookiePolicyAtom);
  const [isModalOpen, setIsModalOpen] = useAtom(cookieModalAtom);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialCookiePolicy = useMemo(() => acceptedCookiePolicy, []);
  const [selectedCookies, setSelectedCookies] = useState<CookieTypeEnum[]>(
    getInitiallySelectedCookies(acceptedCookiePolicy),
  );

  useEffect(() => {
    if (isModalOpen) {
      setSelectedCookies(getInitiallySelectedCookies(acceptedCookiePolicy));
    }
  }, [acceptedCookiePolicy, isModalOpen]);

  useEffect(() => {
    if (acceptedCookiePolicy !== initialCookiePolicy) {
      document.cookie = `${CookieStorageKey}=${acceptedCookiePolicy}; path=/; SameSite=Strict;`;
      setCookie('hasAcceptedAnalytics', acceptedCookiePolicy.includes(CookieTypeEnum.Analytics));
      setCookie('hasAcceptedBehavioral', acceptedCookiePolicy.includes(CookieTypeEnum.Behavioral));
      const hasAnalytics = acceptedCookiePolicy.includes(CookieTypeEnum.Analytics);
      const hasBehavioral = acceptedCookiePolicy.includes(CookieTypeEnum.Behavioral);
      sendGTMEvent({
        event: 'E_setAcceptedCookiePolicy',
        user_cookies_hasAcceptedAnalytics: hasAnalytics,
        user_cookies_hasAcceptedBehavioral: hasBehavioral,
      });
    }
  }, [initialCookiePolicy, acceptedCookiePolicy]);

  const currentCookiePolicy = useMemo(
    () =>
      cookieSettings
        .map((setting) => {
          if (setting.required) {
            return setting.type;
          }

          return selectedCookies.includes(setting.type) ? setting.type : null;
        })
        .filter(Boolean)
        .join('') as AcceptedCookiePolicyEnum,

    [selectedCookies],
  );

  const onOpen = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  const onClose = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleCookieSettingClick = useCallback((setting: CookieSettingType) => {
    if (setting.required) {
      return;
    }

    setSelectedCookies((prev) =>
      prev.includes(setting.type)
        ? prev.filter((cookie) => cookie !== setting.type)
        : [...prev, setting.type],
    );
  }, []);

  const handleConfirm = useCallback(() => {
    setAcceptedCookiePolicy(currentCookiePolicy);
    onClose();
  }, [currentCookiePolicy, setAcceptedCookiePolicy, onClose]);

  const handleAcceptAll = useCallback(() => {
    setAcceptedCookiePolicy(AcceptedCookiePolicyEnum.All);
  }, [setAcceptedCookiePolicy]);

  return (
    <>
      <CookieCard
        onOpen={onOpen}
        acceptedCookiePolicy={acceptedCookiePolicy}
        onAcceptAll={handleAcceptAll}
      />
      <CookiePreferences
        isModalOpen={isModalOpen}
        onClose={onClose}
        handleCookieSettingClick={handleCookieSettingClick}
        handleConfirm={handleConfirm}
        selectedCookies={selectedCookies}
      />
    </>
  );
};

export default CookieBanner;
